import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  FetchPolicy,
  HttpLink,
  InMemoryCache,
  MaybeMasked,
  NormalizedCacheObject,
  Observable,
  Operation,
  OperationVariables,
  ServerError,
  TypedDocumentNode,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';
import * as Sentry from '@sentry/react';
import { SentryLink } from 'apollo-link-sentry';
import { DocumentNode, GraphQLFormattedError, print } from 'graphql';

import { AUTH_TOKEN_CACHE_KEY } from '@/auth/utils/authTokenKey';
import {
  expireCognitoSession,
  forceRefreshApiToken,
  getApiToken,
  isCognitoSessionActive,
  isCognitoSessionExpiring,
} from '@/auth/utils/cognitoSession';
import { isMfaGateResponse, routeToMfaSetup } from '@/auth/utils/mfaGate';
import {
  getChannelListItemUserOperation,
  getChatHeaderUserOperation,
} from '@/messaging/operationNames';
import { clearCache, getCache } from '@/utils';

import ERROR_CODES, { ErrorCode } from './apiErrorCodes';
import { getDefaultErrorMessage } from './apiErrorMessages';

// The page-level Apollo client reads the auth token on every request through
// getApiToken (see authLink): the legacy token from localStorage, or the
// Cognito ID token while a Cognito session is active (ENG-6102). DO NOT swap to a closure-
// captured token here — we had pain in the past where the token was
// captured at GraphQLClient construction time and stayed stale after
// post-signup authentication, breaking "view settings of just-created
// team" and other mid-session flows. The per-request read is the
// load-bearing fix for those issues. AUTH_TOKEN_CACHE_KEY lives in its
// own leaf module to keep this file out of the auth/utils import cycle.

export { default as ERROR_CODES } from './apiErrorCodes';
export type { ErrorCode } from './apiErrorCodes';

export const OTP_METHODS = {
  SMS: 'sms',
  APP: 'app',
  EMAIL: 'email',
} as const;

export type OtpMethod = (typeof OTP_METHODS)[keyof typeof OTP_METHODS];

export function hasErrorCode(err: unknown, code: ErrorCode): boolean {
  let errors: readonly GraphQLFormattedError[] = [];
  if (err instanceof ApolloError) {
    errors = err.graphQLErrors;
  } else if (err instanceof APIError) {
    errors = err.rawError;
  } else if (err instanceof UnauthorizedError) {
    return err.code === code;
  } else if (Array.isArray(err)) {
    errors = err;
  }

  for (const gqlError of errors) {
    if (gqlError?.extensions?.code === code) return true;
  }

  return false;
}

// True when EVERY GraphQL error carries `code` and there is no network error —
// i.e. the code is the *only* thing wrong. Use instead of hasErrorCode when the
// call site swallows the error rather than branching on it: hasErrorCode is an
// any-match, so a batch carrying one expected code alongside a genuinely
// unexpected sibling would be swallowed whole (ENG-5628).
export function hasOnlyErrorCode(err: unknown, code: ErrorCode): boolean {
  if (!(err instanceof ApolloError)) return false;
  if (err.networkError) return false;
  return (
    err.graphQLErrors.length > 0 &&
    err.graphQLErrors.every((gqlError) => gqlError?.extensions?.code === code)
  );
}

// Extract the first GraphQL error code from an APIError/ApolloError, or
// undefined if the error has no code. Use when the call site needs to
// branch on the actual code (vs. checking a known code with hasErrorCode).
export function firstErrorCode(err: unknown): string | undefined {
  let errors: readonly GraphQLFormattedError[] = [];
  if (err instanceof ApolloError) {
    errors = err.graphQLErrors;
  } else if (err instanceof APIError) {
    errors = err.rawError;
  } else if (err instanceof UnauthorizedError) {
    return err.code;
  } else if (Array.isArray(err)) {
    errors = err;
  }
  for (const gqlError of errors) {
    const code = gqlError?.extensions?.code;
    if (typeof code === 'string') return code;
  }
  return undefined;
}

// Extract human-readable messages from an APIError/ApolloError (or raw GraphQL
// error array) for surfacing to the user (e.g. as a form-level error). Falls
// back to the error's own message when there are no GraphQL errors (e.g. a
// network error). ENG-5267.
export function errorMessages(err: unknown): string[] {
  let errors: readonly GraphQLFormattedError[] = [];
  if (err instanceof ApolloError) {
    errors = err.graphQLErrors;
  } else if (err instanceof APIError) {
    errors = err.rawError;
  } else if (Array.isArray(err)) {
    errors = err;
  }
  if (errors.length > 0) {
    return errors.map((gqlError) => gqlError.message);
  }
  return [err instanceof Error ? err.message : 'Something went wrong'];
}

// Codes the API gives errors that describe a client or server bug rather than
// anything the user can act on: schema errors (a variable the client never
// sent, a field that does not exist), "Must provide an operation", and any
// unexpected resolver error when the API runs with DEBUG on.
const NON_USER_FACING_CODES: ReadonlySet<string> = new Set([
  ERROR_CODES.UNKNOWN,
  'graphql.no_operation',
]);

// Permission errors render the policy that failed ("Permission
// consultation:update_rates:1 required."), which names internal permissions
// and ids.
// Same string as useErTransitions' permission copy, curly apostrophe included.
const PERMISSION_DENIED_MESSAGE = 'You don’t have permission to do that.';

// What to tell the user when a request fails: the crafted copy for the error's
// code, else the API's own message, else `fallback`. Only errors tagged with a
// domain code are shown. The API tags those through validation.NewError, whose
// messages are written for the caller; the many validation errors still raised
// under unknown.unknown get `fallback` until they are given real codes. A 500 or
// a network failure carries no GraphQL errors and gets `fallback` too. Takes
// ApolloError and APIError alike, so it does not depend on which client made the
// call. ENG-5322.
//
// The domain catch-alls (consultation.unknown, expertrequest.unknown, …) pass
// this filter, so their messages are shown verbatim. That holds only while the
// API raises them through validation.NewError/Errorf with copy written for the
// caller: a <domain>.unknown must never wrap an internal error's text.
export function apiErrorMessage(err: unknown, fallback: string): string {
  return apiErrorMessages(err, fallback)[0];
}

// apiErrorMessage for a call site that shows every error in a multi-error
// response: each user-facing message once, in order, or [fallback] when there is
// none.
export function apiErrorMessages(err: unknown, fallback: string): string[] {
  let errors: readonly GraphQLFormattedError[] = [];
  if (err instanceof ApolloError) {
    errors = err.graphQLErrors;
  } else if (err instanceof APIError) {
    errors = err.rawError;
  }
  const messages = new Set<string>();
  for (const gqlError of errors) {
    const code = gqlError.extensions?.code;
    if (typeof code !== 'string' || NON_USER_FACING_CODES.has(code)) continue;
    if (code === ERROR_CODES.PERMISSION_NOT_ALLOWED) {
      messages.add(PERMISSION_DENIED_MESSAGE);
    } else {
      messages.add(getDefaultErrorMessage(code) ?? (gqlError.message || fallback));
    }
  }
  return messages.size > 0 ? [...messages] : [fallback];
}

interface UnauthorizedResponse {
  code: ErrorCode;
  reason?: string;
  remaining_attempts?: number;
  mfa_group_id?: string;
  otp_method?: OtpMethod;
  otp_masked_address?: string;
}

export class UnauthorizedError extends Error {
  isPermissionError: boolean;
  code: ErrorCode;
  remainingAttempts?: number;
  mfaGroupId?: string;
  otpMethod?: OtpMethod;
  otpMaskedAddress?: string;

  constructor(message: string, response: UnauthorizedResponse) {
    super(message);
    this.name = this.constructor.name;
    this.code = response.code;
    this.remainingAttempts = response.remaining_attempts;
    this.mfaGroupId = response.mfa_group_id;
    this.otpMethod = response.otp_method;
    this.otpMaskedAddress = response.otp_masked_address;
    this.isPermissionError = true;
  }
}

// Keys whose values must never ride along on an error object. `pass` covers
// resetPassword's `newPass`, `invite` is createUser's invitation token, and a
// key named exactly `code` is socialConnectVerify's mailed code (ENG-6192);
// `country_code` and the like are not secrets.
const SECRET_VARIABLE_KEY = /pass|secret|token|otp|invite|^code$/i;

// APIErrors get logged and reported (console capture, replay breadcrumbs),
// and createUser's and resetPassword's variables carry plaintext passwords
// (ENG-5760).
function redactSecrets(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactSecrets);
  if (value === null || typeof value !== 'object' || value instanceof Date) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, v]) => [
      key,
      SECRET_VARIABLE_KEY.test(key) ? '[redacted]' : redactSecrets(v),
    ])
  );
}

export class APIError extends Error {
  isPermissionError: boolean;
  query: string;
  rawError: readonly GraphQLFormattedError[];
  variables?: OperationVariables;

  constructor(
    errors: readonly GraphQLFormattedError[],
    query: string,
    variables?: OperationVariables
  ) {
    const { message } = errors[0];
    super(`GraphQL Error: ${message}`);
    this.rawError = errors;
    this.query = query;
    this.variables = redactSecrets(variables) as OperationVariables | undefined;
    this.isPermissionError = hasErrorCode(errors, ERROR_CODES.PERMISSION_NOT_ALLOWED);
  }
}

export function basicAuth(username: string, password: string): string {
  const utf8Bytes = new TextEncoder().encode(`${username}:${password}`);
  const base64String = utf8Bytes.toBase64();
  return `Basic ${base64String}`;
}

export function bearerAuth(token: string): string {
  return `Bearer ${token}`;
}

// Error codes that mean "the cached JWT no longer authenticates the user"
// — distinct from credential-validation 401s (auth.invalid_credentials,
// auth.mfa_required) which login forms handle inline.
//
// auth.api_key_invalid is included because pre-ENG-XXXX the API's
// TokenAuthMiddleware overwrote the JWT error with the API-key-digest
// error for any JWT-shaped token that failed validation. Old API
// deployments — and any in-flight requests during rollout — will still
// report expired JWTs that way. Safe to remove once every prod API
// instance is past the middleware_token.go fix.
const SESSION_EXPIRY_CODES: ReadonlySet<ErrorCode> = new Set([
  ERROR_CODES.AUTH_TOKEN_EXPIRED,
  ERROR_CODES.AUTH_TOKEN_INVALID,
  ERROR_CODES.AUTH_API_KEY_INVALID,
]);

export function isSessionExpiryCode(code: ErrorCode): boolean {
  return SESSION_EXPIRY_CODES.has(code);
}

// True when an ApolloError carries a 401 with a session-expiry code in its
// networkError body. Used by the Sentry beforeSend filter for the brief
// window before handleError redirects to /login.
export function isApolloSessionExpiry(err: ApolloError): boolean {
  return isSessionExpiryResponse(err.networkError);
}

// True for the 401-with-expiry-code response itself (what links see, before
// Apollo wraps it in an ApolloError).
function isSessionExpiryResponse(networkError: unknown): boolean {
  if (!networkError || typeof networkError !== 'object' || !('result' in networkError)) {
    return false;
  }
  const serverErr = networkError as ServerError;
  if (serverErr.statusCode !== 401) return false;
  const code = (serverErr.result as UnauthorizedResponse | undefined)?.code;
  return code !== undefined && isSessionExpiryCode(code);
}

/**
 * ENG-6102: while a Cognito session is active, a 401 with an expiry or invalid
 * code gets one forced refresh and one retry of the operation. Covers a token
 * the api judged expired a little before the client did, and a revoked one
 * that a refresh replaces. When the retry is refused too, or the refresh finds
 * no session, the link itself expires the session, so raw `graphql.client`
 * users are covered as well as the GraphQLClient wrappers. Inert without a
 * Cognito session.
 */
export const cognitoRetryLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      let retried = false;
      let closed = false;
      let subscription: { unsubscribe: () => void } | undefined;
      const run = () => {
        subscription = forward(operation).subscribe({
          next: (value) => observer.next(value),
          complete: () => observer.complete(),
          error: (err: unknown) => {
            if (!isCognitoSessionActive() || !isSessionExpiryResponse(err)) {
              observer.error(err);
              return;
            }
            if (retried) {
              void expireCognitoSession();
              observer.error(err);
              return;
            }
            retried = true;
            forceRefreshApiToken().then(
              (token) => {
                // Unsubscribed while the refresh was pending: no new request.
                if (closed) return;
                if (!token) {
                  void expireCognitoSession();
                  observer.error(err);
                  return;
                }
                operation.setContext(({ headers }: { headers?: Record<string, string> }) => ({
                  headers: { ...headers, authorization: bearerAuth(token) },
                }));
                run();
              },
              // A transient refresh failure: forward it, not the 401, so a
              // network blip reads as a network error and keeps the session.
              (refreshErr: unknown) => {
                if (!closed) observer.error(refreshErr);
              }
            );
          },
        });
      };
      run();
      return () => {
        closed = true;
        subscription?.unsubscribe();
      };
    })
);

/**
 * ENG-6103: the api's MFA gate (ENG-6095) answers 403 auth.mfa_setup_required
 * to a Cognito user who still has to enrol a factor. Not a session end, so it
 * never reaches the 401 retry or handleSessionExpiry: send the user to
 * enrolment and let the error through to the caller unchanged. Interactive
 * sign-in opts out and handles the code itself (`skipMfaGateRedirect`).
 */
export const mfaGateLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next: (value) => observer.next(value),
        complete: () => observer.complete(),
        error: (err: unknown) => {
          // An operation can opt out (context.skipMfaGateRedirect) when its
          // caller handles the gate itself.
          if (isMfaGateResponse(err) && !operation.getContext().skipMfaGateRedirect) {
            routeToMfaSetup();
          }
          observer.error(err);
        },
      });
      return () => subscription.unsubscribe();
    })
);

// When the API tells us the JWT is no longer valid mid-session, drop the
// cached token and bounce to /login so the user lands somewhere coherent
// instead of staring at a half-loaded page. Mirrors the boot-time recovery
// in src/index.tsx — that path already covers full reloads; this covers
// the in-flight-query case (Sentry WEBAPP-BW1/BW2).
function handleSessionExpiry(storedAtSend: string | undefined): void {
  // Cognito: cognitoRetryLink has usually started the expiry already (it is
  // idempotent, so this joins it); it redirects only after the sign-out.
  if (isCognitoSessionActive() || isCognitoSessionExpiring()) {
    void expireCognitoSession();
    return;
  }
  // ENG-6136: the stored token changed while the request was out (login-as,
  // an OTP sign-in, a logout): the refusal is about a superseded credential,
  // not the one stored now.
  if (storedLegacyToken() !== storedAtSend) return;
  clearCache(AUTH_TOKEN_CACHE_KEY);
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/login')) return;
  window.location.assign('/login');
}

function storedLegacyToken(): string | undefined {
  return (getCache(AUTH_TOKEN_CACHE_KEY) as { token?: string } | undefined)?.token;
}

function handleError<TVariables extends OperationVariables = OperationVariables>(
  err: unknown,
  storedAtSend: string | undefined,
  query: DocumentNode,
  variables?: TVariables
) {
  if (!(err instanceof ApolloError)) {
    throw err;
  }

  if (err.networkError) {
    if ('result' in err.networkError) {
      const serverErr = err.networkError as ServerError;
      switch (serverErr.statusCode) {
        case 401: {
          const resp = serverErr.result as UnauthorizedResponse;
          if (isSessionExpiryCode(resp.code)) {
            handleSessionExpiry(storedAtSend);
          }
          throw new UnauthorizedError(resp.reason || '', resp);
        }
      }
    }
  }

  const errors = err.graphQLErrors;
  if (errors.length > 0) {
    throw new APIError(errors, print(query), variables);
  }

  throw err;
}

interface GraphQLClientOptions {
  otp?: string;
  extraHeaders?: Record<string, string>;
  /**
   * ENG-6103: a 403 from the MFA gate is left to the caller instead of sending
   * the page to /mfa_setup. For interactive sign-in, which routes the user
   * itself (or abandons the session) and must not race a navigation.
   */
  skipMfaGateRedirect?: boolean;
}

const RETRY_MAX_ATTEMPTS = 3;

/**
 * Mutations that may be replayed after a failure that left no result.
 *
 * Mutations are excluded from retry by default because replaying one that has
 * already been applied server-side creates a second, different record. An entry
 * here is a claim that this one leaves no *durable state divergence* on replay
 * — which is a weaker claim than "no-op", and the weaker one is what was
 * actually verified. Read that before adding to this list.
 *
 * `actionUpdateUser` qualifies. Every argument it takes is a set-to-value or a
 * merge keyed by a value the client supplies, so a second call lands on the
 * same row with the same contents:
 *
 * - `agreements` is applied one entry at a time by UpdateAgreement
 *   (api/legal/compliance/service/compliance_service.go:198), which finds the
 *   entry by policy string or appends it, then returns *before writing* when
 *   the accepted flag already matches. It also stamps `updated_at` from the
 *   server clock and ignores the client's, so a replay cannot re-date a
 *   consent record — the property that makes this safe on a legal surface.
 * - `signup_type` and `background_check` are scalar assignments.
 *
 * What a replay DOES duplicate: updateUser calls AuthService.UpdateUser
 * unconditionally, ahead of the agreements loop (gql/service/schema/
 * user_gql.go:356), and that writes a `user/updated` activity row and
 * republishes `user:post_update` (auth/service/auth_service.go:299). All three
 * subscribers of that topic — CRM sync, search reindex, expert-request — are
 * upserts, so the fanout is harmless. But the activity log is therefore NOT a
 * reliable record of when an expert accepted; `compliances.agreements
 * .updated_at` is the authoritative one.
 *
 * This is keyed on the operation name, so it covers all six call sites of that
 * document, not just the gate. Adding a non-idempotent argument to
 * UPDATE_USER means revisiting this list.
 *
 * It matters most on /legal/accept (ENG-5570), where this mutation is the only
 * way past a gate that blocks every authenticated route: a blip on a mobile
 * connection there is a wall, not a glitch. Sentry WEBAPP-CCG.
 */
const RETRYABLE_MUTATIONS = ['actionUpdateUser'];

/**
 * Statuses where the request demonstrably never reached the application: the
 * load balancer answered on its own behalf. Distinct from a 500, which is the
 * app having made a decision — replaying that may only amplify load.
 *
 * These are worth retrying for an allowlisted mutation because the API rolling
 * out a new revision is exactly when they appear, and ENG-5570 put ~55k active
 * experts behind a single mutation. A deploy-window 502 would otherwise hand
 * every one of them an error blaming their acceptance.
 */
const GATEWAY_STATUSES = [502, 503, 504];

function statusOf(error: unknown): number | undefined {
  const cause = error instanceof ApolloError ? error.networkError : error;
  return (cause as Partial<ServerError> | null | undefined)?.statusCode;
}

/** True when a gateway shed the request before the application saw it. */
export function isGatewayFailure(error: unknown): boolean {
  const status = statusOf(error);
  return status !== undefined && GATEWAY_STATUSES.includes(status);
}

/**
 * True for a failure that never reached the server, or whose response never
 * came back. Every modern engine surfaces these as TypeError — "Failed to
 * fetch" in Chrome, "Load failed" in WebKit/iOS, "NetworkError when attempting
 * to fetch resource" in Firefox.
 *
 * Unwraps ApolloError so UI code can ask the same question the retry predicate
 * asks: callers of GraphQLClient see the wrapper, RetryLink sees the cause.
 */
export function isWireLevelFailure(error: unknown): boolean {
  const cause = error instanceof ApolloError ? error.networkError : error;
  if (!(cause instanceof TypeError)) return false;

  // Defensive: TypeErrors don't carry these, but the safety-net survives any
  // future broadening of the entry condition above.
  const networkError = cause as Partial<ServerError> & { bodyText?: string };
  if (networkError.statusCode !== undefined) return false;
  if (networkError.result !== undefined) return false;
  if (networkError.bodyText !== undefined) return false;
  return true;
}

// Predicate for RetryLink. Retries only true wire-level fetch failures.
// Anything that isn't a TypeError falls through without a retry — including
// DOMException AbortError/CancelError from subscription unsubscribe (already
// in src/index.tsx ignoreErrors, keeping the two filters consistent) and any
// non-fetch error class. HTTP 4xx/5xx (which carry statusCode +
// result/bodyText) are server-side decisions; retrying may amplify load.
// Mutations are retried only when listed in RETRYABLE_MUTATIONS above. Skip
// the high-fanout chat user queries (one per channel render); a single blip
// otherwise bursts N parallel retry chains across a chat list.
export function shouldRetryRequest(error: unknown, operation: Operation): boolean {
  if (!error) return false;

  const definition = getMainDefinition(operation.query);
  const isMutation =
    definition.kind === 'OperationDefinition' && definition.operation === 'mutation';
  const isAllowlisted = RETRYABLE_MUTATIONS.includes(operation.operationName);

  if (isMutation && !isAllowlisted) return false;

  if (
    operation.operationName === getChannelListItemUserOperation ||
    operation.operationName === getChatHeaderUserOperation
  ) {
    return false;
  }

  // Gateway statuses are retried only for the allowlisted operations, whose
  // replay safety has been established one at a time. Queries keep the
  // narrower wire-level-only rule they shipped with.
  if (isAllowlisted && isGatewayFailure(error)) return true;

  return isWireLevelFailure(error);
}

export class GraphQLClient {
  client: ApolloClient<NormalizedCacheObject>;
  url: string;

  constructor(
    url: string,
    auth?: string,
    { otp, extraHeaders = {}, skipMfaGateRedirect = false }: GraphQLClientOptions = {}
  ) {
    const httpLink = new HttpLink({ uri: `${url}/graphql` });
    // see: https://www.apollographql.com/docs/react/api/link/apollo-link-remove-typename
    const removeTypenameLink = removeTypenameFromVariables();

    const authLink = setContext(async (_, { headers }) => {
      // When the caller passed an explicit `auth`, use it as-is (e.g.,
      // authSession.createClient(token) for one-off post-OTP mutations).
      // Otherwise ask getApiToken — PER request, so the page-level client
      // (constructed at app start when no token exists yet) picks up a token
      // stored later by authSession.authenticate, and a Cognito ID token is
      // refreshed before it is sent. Without this, every post-signup
      // navigation would 401 until the user reloads the page.
      let authorization = auth;
      if (!authorization) {
        const token = await getApiToken();
        if (token) {
          authorization = bearerAuth(token);
        }
      }
      return {
        headers: {
          ...headers,
          ...(authorization && { authorization }),
          ...(otp && { 'x-onfrontiers-auth-otp': otp }),
          ...extraHeaders,
        },
      };
    });

    // Sits between SentryLink and httpLink so the retries are transparent to
    // SentryLink's per-operation breadcrumb — Sentry sees a single operation
    // per user-initiated query. The `attempts` function form is used (instead
    // of `{ max, retryIf }`) so we can emit our own breadcrumb on each retry
    // decision; without it, retries-that-succeed are invisible to ops and we
    // lose the ability to detect a degrading API.
    const retryLink = new RetryLink({
      delay: { initial: 300, max: 3000, jitter: true },
      attempts: (count, operation, error) => {
        if (count >= RETRY_MAX_ATTEMPTS) return false;
        if (!shouldRetryRequest(error, operation)) return false;
        Sentry.addBreadcrumb({
          category: 'graphql.retry',
          level: 'info',
          message: `Retrying ${operation.operationName} after transient network failure`,
          data: {
            operationName: operation.operationName,
            attempt: count,
            errorMessage: error instanceof Error ? error.message : String(error),
          },
        });
        return true;
      },
    });

    this.url = url;
    this.client = new ApolloClient({
      link: from([
        removeTypenameLink,
        authLink,
        mfaGateLink,
        // An explicit `auth` is a fixed token the caller owns: no refresh.
        ...(auth ? [] : [cognitoRetryLink]),
        new SentryLink({
          shouldHandleOperation: (operation) => {
            const ignoreOperations = [getChannelListItemUserOperation, getChatHeaderUserOperation];
            return !ignoreOperations.includes(operation.operationName);
          },
        }),
        retryLink,
        httpLink,
      ]),
      cache: new InMemoryCache(),
      defaultContext: skipMfaGateRedirect ? { skipMfaGateRedirect: true } : undefined,
      devtools: {
        enabled: import.meta.env.MODE === 'development',
      },
    });
  }

  async query<T = never, TVariables extends OperationVariables = OperationVariables>(
    query: TypedDocumentNode<T, TVariables>,
    variables?: TVariables,
    options?: { fetchPolicy?: FetchPolicy }
  ): Promise<MaybeMasked<T>> {
    const storedAtSend = storedLegacyToken();
    try {
      const { data } = await this.client.query({ query, variables, ...options });
      return data;
    } catch (err: unknown) {
      handleError(err, storedAtSend, query, variables);
      throw err;
    }
  }

  async mutate<T = never, TVariables extends OperationVariables = OperationVariables>(
    query: TypedDocumentNode<T, TVariables>,
    variables?: TVariables,
    // A caller that imposes its own deadline passes the signal that enforces
    // it, so abandoning the wait also frees the socket and stops RetryLink
    // rather than leaving a second write racing the first.
    options?: { signal?: AbortSignal }
  ): Promise<MaybeMasked<T>> {
    const storedAtSend = storedLegacyToken();
    try {
      const { data } = await this.client.mutate({
        mutation: query,
        variables,
        ...(options?.signal && { context: { fetchOptions: { signal: options.signal } } }),
      });
      return data!;
    } catch (err: unknown) {
      handleError(err, storedAtSend, query, variables);
      throw err;
    }
  }

  fromAuth = (auth: string, options?: GraphQLClientOptions): GraphQLClient =>
    new GraphQLClient(this.url, auth, options);
}
